/**
 * RechargeBlock — quarantined credit-recharge widget for ProfileComplete v2.0.
 *
 * The payment flow is preserved VERBATIM from the legacy ProfileComplete.js
 * because real money is involved and the integration is already tested.
 * Visual layer is upgraded; payment logic is identical.
 *
 * Flow (cross-tenant redirect — NOT a Razorpay popup):
 *   1. GET  /credit-recharge/plans-balance  → balance + plans
 *   2. POST /credit-recharge/initiate       → order_id + payment_confirm_token
 *   3. POST /credit-recharge/generate-token → centralized payment_url
 *   4. window.location.href = payment_url   (full redirect)
 *
 * Skill compliance:
 *   - Hard rule 6 — no hex; severity Tags + var tokens only
 *   - P15 — uniform white cards, primary-50 icon chip
 *   - P22 — no incremental loading needed (typically <10 plans)
 */
import { useEffect, useRef, useState } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Skeleton } from 'primereact/skeleton';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { RadioButton } from 'primereact/radiobutton';
import axiosInstance from '../../config/axios.config';

// Canonical Indian currency formatter — must match CreditWallet.js / skill rule.
// ₹250 · ₹1,25,000 · ₹1,23,45,678 (Indian grouping, no trailing zeros).
const formatINR = (amount) =>
    `₹${parseFloat(amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

// Per-credit cost — shown 2 decimals because it's typically < ₹1.
const formatPerCredit = (amount, credits) => {
    if (!credits) return '';
    return `₹${(parseFloat(amount || 0) / credits).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
};

const RechargeBlock = ({ userId, sourcePath = '/app/profile/complete' }) => {
    const toast = useRef(null);
    const [loading, setLoading] = useState(true);
    const [balance, setBalance] = useState(0);
    const [plans, setPlans] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState(null);
    const [processing, setProcessing] = useState(false);

    /* ── Load wallet data on mount ───────────────────────────────────── */
    useEffect(() => {
        const load = async () => {
            try {
                const res = await axiosInstance.get('/credit-recharge/plans-balance');
                if (res.data?.success) {
                    setBalance(res.data.data?.current_balance ?? 0);
                    setPlans(res.data.data?.plans || []);
                }
            } catch {
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to load wallet data. You can still submit your profile.',
                    life: 3500,
                });
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    /* ── Initialize payment (preserved verbatim) ─────────────────────── */
    const initializePayment = async () => {
        if (!selectedPlan) {
            toast.current?.show({
                severity: 'error',
                summary: 'Select a plan',
                detail: 'Please select a recharge plan before continuing.',
                life: 3000,
            });
            return;
        }

        try {
            setProcessing(true);
            const response = await axiosInstance.post('/credit-recharge/initiate', {
                recharge_price_id: selectedPlan.id,
                user_id: userId,
            });

            if (response.data && response.data.company_name) {
                const tokenResponse = await axiosInstance.post('/credit-recharge/generate-token', {
                    order_id: response.data.order_id,
                    source_domain: window.location.hostname,
                    source_path: sourcePath,
                    payment_confirm_token: response.data.payment_confirm_token,
                });

                if (tokenResponse.data.success) {
                    window.location.href = tokenResponse.data.payment_url;
                    return; // redirect underway, leave processing=true
                }
                throw new Error('Payment initialization failed');
            } else {
                throw new Error('Company information missing - cannot process payment');
            }
        } catch (error) {
            // Map backend / network errors to user-friendly copy (preserved logic)
            let detail = 'Error in initiating payment. Please try again.';
            if (error.message?.includes('Company information missing')) {
                detail = 'Error in initiating payment. Please try again or contact support.';
            } else if (error.response) {
                if (error.response.status === 401) detail = 'Your session has expired. Please log in again.';
                else if (error.response.status === 403) detail = 'You do not have permission to perform this action.';
                else if (error.response.status === 404) detail = 'The selected plan is no longer available. Please refresh and try again.';
                else if (error.response.status >= 500) detail = 'Service temporarily unavailable. Please try again after some time.';
            } else if (error.request) {
                detail = 'Connection error. Please check your internet and try again.';
            }
            toast.current?.show({ severity: 'error', summary: 'Payment failed', detail, life: 5000 });
            setProcessing(false);
        }
    };

    /* ── Render ──────────────────────────────────────────────────────── */
    const lowBalance = balance <= 0;

    return (
        <Card
            className="shadow-1"
            pt={{ body: { className: 'p-3' }, content: { className: 'p-0' } }}
        >
            <Toast ref={toast} />

            {/* Heading + balance */}
            <div className="flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                <div className="flex align-items-center gap-3">
                    <div
                        className="flex align-items-center justify-content-center border-round flex-shrink-0"
                        style={{ width: '2.75rem', height: '2.75rem', backgroundColor: 'var(--primary-50)' }}
                    >
                        <i className="pi pi-wallet text-primary" aria-hidden="true" />
                    </div>
                    <div>
                        <div className="text-900 font-semibold text-sm">Credit balance</div>
                        {loading ? (
                            <Skeleton width="6rem" height="1.5rem" className="mt-1" />
                        ) : (
                            <div className="text-900 text-xl font-semibold">{formatINR(balance)}</div>
                        )}
                    </div>
                </div>
                {!loading && (
                    <Tag
                        value={lowBalance ? 'Top up needed' : 'Wallet active'}
                        severity={lowBalance ? 'warning' : 'success'}
                        className="text-xs"
                    />
                )}
            </div>

            {/* Plans grid */}
            {loading ? (
                <div className="grid">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="col-12 sm:col-6 lg:col-4 mb-2">
                            <Skeleton width="100%" height="5rem" />
                        </div>
                    ))}
                </div>
            ) : plans.length === 0 ? (
                <Message
                    severity="info"
                    text="No recharge plans available right now. You can still submit your profile and recharge later from the wallet section."
                    className="w-full"
                />
            ) : (
                <>
                    <div className="text-700 text-sm mb-2">Pick a plan to top up your wallet:</div>
                    <div className="grid">
                        {plans.map((plan) => (
                            <PlanCard
                                key={plan.id}
                                plan={plan}
                                selected={selectedPlan?.id === plan.id}
                                onSelect={setSelectedPlan}
                                disabled={processing}
                            />
                        ))}
                    </div>

                    <div className="flex flex-wrap gap-2 align-items-center justify-content-end mt-3">
                        <span className="text-500 text-xs flex-grow-1">
                            You'll be redirected to our secure payment gateway.
                        </span>
                        <Button
                            label="Recharge now"
                            icon="pi pi-credit-card"
                            onClick={initializePayment}
                            loading={processing}
                            disabled={!selectedPlan || processing}
                        />
                    </div>
                </>
            )}
        </Card>
    );
};

/* ── PlanCard — verbatim from CreditWallet.js so the design stays in sync ── */
const PlanCard = ({ plan, selected, onSelect, disabled }) => {
    const inputId = `plan-${plan.id}`;
    return (
        <div className="col-12 md:col-6 lg:col-3">
            <Card>
                <div className="flex align-items-start gap-2">
                    <RadioButton
                        inputId={inputId}
                        checked={selected}
                        onChange={() => onSelect(plan)}
                        disabled={disabled}
                    />
                    <label htmlFor={inputId} className="flex flex-column flex-1 min-w-0 cursor-pointer">
                        <span className="text-900 text-base font-semibold">{plan.credits} Credits</span>
                        <span className="text-primary text-lg font-bold">{formatINR(plan.amount)}</span>
                        <span className="text-500 text-xs mt-1">
                            {formatPerCredit(plan.amount, plan.credits)} / credit
                        </span>
                        {plan.description && (
                            <span className="text-500 text-xs mt-1">{plan.description}</span>
                        )}
                    </label>
                </div>
            </Card>
        </div>
    );
};

export default RechargeBlock;
